import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LiveScreen, QueryState, StatusBadge, StatusPill, formatDate, formatDateTime, formatNumber, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc, describeError } from '@/api/resources';
import { toast } from '@/lib/toast';

interface AlertRow {
  id: string; alert_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  alert_type: string; severity: string; summary: string | null; detected_on: string | null; change_pct: number | null;
  notification_status: string | null; acknowledged_by_name: string | null; acknowledged_at: string | null; created_at: string;
}
interface JobRow {
  id: string; job_kind: string; plot_code: string | null; window_from: string | null; window_to: string | null; priority: number;
  is_critical: boolean; state: string; attempts: number; max_attempts: number; next_run_at: string | null;
  error_code: string | null; error_message: string | null; created_at: string; completed_at: string | null;
}
interface BatchRow {
  id: string; batch_code: string | null; batch_type: string; provider_key: string | null; state: string; requested_by_name: string | null;
  requested_at: string | null; plot_count: number | null; job_count: number | null; processing_units: number | null; error_count: number | null;
}
interface UsageRow { provider_id: string; period_month: string; api_operation: string; requests: number; processing_units: number; failures: number; avg_processing_ms: number | null }

type Tab = 'alerts' | 'jobs' | 'batches' | 'usage';

/** A queued job this long past its run time means nothing is draining the queue. */
const STALL_MINUTES = 30;

const words = (value?: string | null) => (value ? value.replace(/_/g, ' ') : '--');

function Alerts() {
  const queryClient = useQueryClient();
  const [stat, setStat] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const rows = useRows<AlertRow>('satellite_alerts', { orderBy: { column: 'created_at', ascending: false } });
  const STATS = useMemo(() => [
    { key: 'all', label: 'Alerts', tone: 'neutral' as const, match: () => true },
    { key: 'open', label: 'Not acknowledged', tone: 'error' as const, match: (row: AlertRow) => !row.acknowledged_at },
    { key: 'critical', label: 'Critical', tone: 'warn' as const, match: (row: AlertRow) => row.severity === 'critical' },
    { key: 'ack', label: 'Acknowledged', tone: 'ok' as const, match: (row: AlertRow) => Boolean(row.acknowledged_at) },
  ], []);

  async function acknowledge(row: AlertRow) {
    setBusy(row.id);
    try {
      await callRpc('fn_acknowledge_satellite_alert', { p_alert_id: row.id });
      toast.success('Acknowledged.');
      void queryClient.invalidateQueries({ queryKey: ['satellite_alerts'] });
    } catch (error) {
      toast.error(describeError(error as { message?: string; code?: string }));
    } finally {
      setBusy(null);
    }
  }

  const columns: Array<Column<AlertRow>> = [
    { key: 'created_at', label: 'Raised', render: (row) => <span className="cell-stack"><span>{formatDateTime(row.created_at)}</span><small className="muted">{row.alert_code ?? ''}</small></span> },
    { key: 'plot_code', label: 'Plot', render: (row) => <span className="cell-stack"><strong>{row.plot_code ?? '--'}</strong><small className="muted">{[row.farmer_name, row.village_name].filter(Boolean).join(' · ')}</small></span> },
    { key: 'alert_type', label: 'What', render: (row) => <span className="cell-stack"><span>{words(row.alert_type)}</span>{row.summary && <small className="muted">{row.summary}</small>}</span> },
    { key: 'severity', label: 'Severity', render: (row) => <StatusPill value={row.severity} /> },
    { key: 'change_pct', label: 'Change', align: 'right', render: (row) => (row.change_pct == null ? '--' : `${formatNumber(row.change_pct, 1)}%`) },
    { key: 'notification_status', label: 'Notified', render: (row) => words(row.notification_status) },
    { key: 'acknowledged_at', label: 'Acknowledged', render: (row) => (row.acknowledged_at
      ? <span className="cell-stack"><span>{row.acknowledged_by_name ?? '--'}</span><small className="muted">{formatDateTime(row.acknowledged_at)}</small></span>
      : <button disabled={busy === row.id} onClick={(event) => { event.stopPropagation(); void acknowledge(row); }} type="button">Acknowledge</button>) },
  ];
  return (
    <>
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="No satellite alert has been raised." query={rows}>
        <DataGrid columns={columns} exportName="satellite-alerts" rows={applyStat(rows.data ?? [], STATS, stat)} screenKey="plot.monitoring" />
      </QueryState>
    </>
  );
}

function Jobs() {
  const [stat, setStat] = useState<string | null>(null);
  const rows = useRows<JobRow>('satellite_jobs', { orderBy: { column: 'created_at', ascending: false } });
  const stalled = useMemo(() => (row: JobRow) => row.state === 'queued' && Boolean(row.next_run_at)
    && Date.now() - new Date(String(row.next_run_at)).getTime() > STALL_MINUTES * 60_000, []);
  const STATS = useMemo(() => [
    { key: 'all', label: 'Jobs', tone: 'neutral' as const, match: () => true },
    { key: 'queued', label: 'Queued', tone: 'info' as const, match: (row: JobRow) => row.state === 'queued' },
    { key: 'stalled', label: `Waiting > ${STALL_MINUTES} min`, tone: 'error' as const, match: stalled },
    { key: 'retry', label: 'Retrying', tone: 'warn' as const, match: (row: JobRow) => row.state === 'retry_wait' },
    { key: 'dead', label: 'Given up', tone: 'error' as const, match: (row: JobRow) => row.state === 'dead_letter' },
    { key: 'done', label: 'Completed', tone: 'ok' as const, match: (row: JobRow) => row.state === 'completed' },
  ], [stalled]);
  const anyStalled = (rows.data ?? []).some(stalled);
  const columns: Array<Column<JobRow>> = [
    { key: 'created_at', label: 'Queued', render: (row) => formatDateTime(row.created_at) },
    { key: 'job_kind', label: 'Kind', render: (row) => <span className="cell-stack"><span>{words(row.job_kind)}</span>{row.is_critical && <small className="muted">critical</small>}</span> },
    { key: 'plot_code', label: 'Plot', render: (row) => row.plot_code ?? '--' },
    { key: 'window_from', label: 'Window', render: (row) => (row.window_from ? `${formatDate(row.window_from)} – ${formatDate(row.window_to)}` : '--') },
    { key: 'state', label: 'State', render: (row) => <span className="cell-stack"><StatusPill value={row.state} />{row.error_message && <small className="muted">{row.error_message}</small>}</span> },
    { key: 'attempts', label: 'Tries', align: 'right', render: (row) => `${row.attempts} / ${row.max_attempts}` },
    { key: 'next_run_at', label: 'Due', render: (row) => (row.next_run_at ? formatDateTime(row.next_run_at) : '--') },
  ];
  return (
    <>
      {anyStalled && (
        <p className="muted">
          <StatusBadge label="Not draining" tone="red" /> Jobs have waited more than {STALL_MINUTES} minutes past their run time. The worker
          is probably not being called: check that <code>sat_worker_key</code> is in Vault and the <code>vanam-satellite-worker</code> job is scheduled.
        </p>
      )}
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="The queue is empty." query={rows}>
        <DataGrid columns={columns} exportName="satellite-jobs" rows={applyStat(rows.data ?? [], STATS, stat)} screenKey="plot.monitoring" />
      </QueryState>
    </>
  );
}

function Batches() {
  const rows = useRows<BatchRow>('satellite_batches', { orderBy: { column: 'created_at', ascending: false } });
  const columns: Array<Column<BatchRow>> = [
    { key: 'batch_code', label: 'Batch', render: (row) => <span className="cell-stack"><strong>{row.batch_code ?? '--'}</strong><small className="muted">{words(row.batch_type)} · {row.provider_key ?? ''}</small></span> },
    { key: 'requested_at', label: 'Requested', render: (row) => <span className="cell-stack"><span>{row.requested_at ? formatDateTime(row.requested_at) : '--'}</span><small className="muted">{row.requested_by_name ?? 'scheduler'}</small></span> },
    { key: 'plot_count', label: 'Plots', align: 'right' },
    { key: 'job_count', label: 'Jobs', align: 'right' },
    { key: 'processing_units', label: 'Units', align: 'right', render: (row) => formatNumber(row.processing_units ?? 0, 1) },
    { key: 'error_count', label: 'Errors', align: 'right' },
    { key: 'state', label: 'State', render: (row) => <StatusPill value={row.state} /> },
  ];
  return (
    <QueryState empty="No batch has run yet." query={rows}>
      <DataGrid columns={columns} exportName="satellite-batches" rows={rows.data ?? []} screenKey="plot.monitoring" />
    </QueryState>
  );
}

function Usage() {
  const rows = useRows<UsageRow>('satellite_usage_by_operation', { orderBy: { column: 'period_month', ascending: false } });
  const data = useMemo(() => (rows.data ?? []).map((row) => ({ ...row, id: `${row.provider_id}-${row.period_month}-${row.api_operation}` })), [rows.data]);
  const columns: Array<Column<UsageRow & { id: string }>> = [
    { key: 'period_month', label: 'Month', render: (row) => formatDate(row.period_month) },
    { key: 'api_operation', label: 'Operation', render: (row) => words(row.api_operation) },
    { key: 'requests', label: 'Requests', align: 'right' },
    { key: 'processing_units', label: 'Processing units', align: 'right', render: (row) => formatNumber(row.processing_units, 1) },
    { key: 'failures', label: 'Failures', align: 'right' },
    { key: 'avg_processing_ms', label: 'Avg ms', align: 'right', render: (row) => formatNumber(row.avg_processing_ms ?? 0, 0) },
  ];
  return (
    <>
      <p className="muted">Measured calls to the imagery providers. Compare processing units with the counter on your Copernicus dashboard.</p>
      <QueryState empty="No provider call has been made yet." query={rows}>
        <DataGrid columns={columns} exportName="satellite-usage" rows={data} screenKey="plot.monitoring" />
      </QueryState>
    </>
  );
}

/**
 * Satellite alerts and the work queue behind them (2026-09-27). The monitoring screen answers "what
 * is happening on my plots"; this one answers "did anyone see the alert" and "is the unattended
 * worker actually running" - a queue that fills every night and never drains looks exactly like a
 * quiet month unless somebody looks at the queue.
 */
export default function SatelliteOpsScreen() {
  const [tab, setTab] = useState<Tab>('alerts');
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'alerts', label: 'Alerts' }, { key: 'jobs', label: 'Job queue' }, { key: 'batches', label: 'Batches' }, { key: 'usage', label: 'Usage' },
  ];
  return (
    <LiveScreen subtitle="Alerts raised from satellite monitoring, and the queue of work that produces them." title="Satellite alerts & jobs">
      <div className="tab-row" role="tablist">
        {tabs.map((item) => (
          <button aria-selected={tab === item.key} className={tab === item.key ? 'active' : ''} key={item.key}
                  onClick={() => setTab(item.key)} role="tab" type="button">{item.label}</button>
        ))}
      </div>
      {tab === 'alerts' && <Alerts />}
      {tab === 'jobs' && <Jobs />}
      {tab === 'batches' && <Batches />}
      {tab === 'usage' && <Usage />}
    </LiveScreen>
  );
}
